/**
 * Soumissions fournisseur — les appels (V1.6-06).
 *
 * Un module à part plutôt qu'une rallonge de `queries.ts`, qui approche les
 * trois mille lignes : la frontière est nette et le fichier reste lisible.
 * Les conventions sont identiques — mêmes clés de cache, même `apiFetch`.
 *
 * Il n'existe ici **aucun appel de comparaison ni d'achat** : V1.6-07
 * comparera, et l'achat viendra plus tard. `useValidateQuote` est la seule
 * porte entre « ce qu'une machine a lu » et « un prix que l'entreprise
 * utilise ».
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client";
import type {
  Page,
  QuoteChunk,
  QuoteLineResponse,
  QuoteProvenance,
  QuoteReceivedVia,
  Quantity,
  SupplierQuote,
  SupplierQuoteDetail,
  SupplierQuoteStatus,
  UnitCode,
  Uuid,
} from "./types";

export const quoteKeys = {
  list: (params: object) => ["supplier-quotes", "list", params] as const,
  one: (id: Uuid) => ["supplier-quotes", id] as const,
  document: (id: Uuid) => ["supplier-quotes", id, "document"] as const,
  provenance: (id: Uuid) => ["supplier-quotes", id, "provenance"] as const,
};

export interface SupplierQuoteParams {
  [key: string]: unknown;
  status?: SupplierQuoteStatus;
  supplier_id?: Uuid;
  rfq_id?: Uuid;
  limit?: number;
  offset?: number;
}

function query(path: string, params?: Record<string, unknown>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const suffix = search.toString();
  return suffix ? `${path}?${suffix}` : path;
}

export function useSupplierQuotes(params: SupplierQuoteParams) {
  return useQuery({
    queryKey: quoteKeys.list(params),
    queryFn: () => apiFetch<Page<SupplierQuote>>(query("/supplier-quotes", params)),
  });
}

export function useSupplierQuote(quoteId: Uuid) {
  return useQuery({
    queryKey: quoteKeys.one(quoteId),
    queryFn: () => apiFetch<SupplierQuoteDetail>(`/supplier-quotes/${quoteId}`),
    enabled: Boolean(quoteId),
  });
}

/**
 * Le texte du document, tel que la chaîne documentaire V1.5-09 l'a extrait.
 *
 * **Aucun second OCR** : ce sont les mêmes extraits que la recherche
 * documentaire utilise, pas une seconde lecture du fichier.
 */
export function useQuoteDocument(quoteId: Uuid, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: quoteKeys.document(quoteId),
    queryFn: () => apiFetch<QuoteChunk[]>(`/supplier-quotes/${quoteId}/document`),
    enabled: Boolean(quoteId) && (options?.enabled ?? true),
  });
}

export function useQuoteProvenance(quoteId: Uuid, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: quoteKeys.provenance(quoteId),
    queryFn: () => apiFetch<QuoteProvenance[]>(`/supplier-quotes/${quoteId}/provenance`),
    enabled: Boolean(quoteId) && (options?.enabled ?? true),
  });
}

export interface CreateSupplierQuoteInput {
  supplier_id: Uuid;
  received_via: QuoteReceivedVia;
  rfq_id?: Uuid | null;
  quote_number?: string | null;
  quote_date?: string | null;
  valid_until?: string | null;
  currency?: string | null;
}

export function useCreateSupplierQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSupplierQuoteInput) =>
      apiFetch<SupplierQuoteDetail>("/supplier-quotes", {
        method: "POST",
        body: {
          supplier_id: input.supplier_id,
          received_via: input.received_via,
          rfq_id: input.rfq_id || null,
          quote_number: input.quote_number || null,
          quote_date: input.quote_date || null,
          valid_until: input.valid_until || null,
          // Jamais devinée : vide reste vide, et le contrôle le signalera.
          currency: input.currency || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["supplier-quotes"] });
    },
  });
}

function useQuoteMutation<TInput extends { quoteId: Uuid }, TResult>(
  path: (input: TInput) => string,
  method: "POST" | "PATCH" | "DELETE",
  body?: (input: TInput) => unknown,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      apiFetch<TResult>(path(input), {
        method,
        body: body ? body(input) : undefined,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.one(variables.quoteId) });
      void queryClient.invalidateQueries({ queryKey: ["supplier-quotes", "list"] });
    },
  });
}

export interface QuoteLineInput {
  description?: string | null;
  response?: QuoteLineResponse;
  product_id?: Uuid | null;
  supplier_sku?: string | null;
  manufacturer?: string | null;
  mpn?: string | null;
  quoted_quantity?: Quantity | null;
  unit?: UnitCode | null;
  unit_price?: Quantity | null;
  line_subtotal?: Quantity | null;
  lead_time_days?: number | null;
  notes?: string | null;
}

export function useAddQuoteLine() {
  return useQuoteMutation<{ quoteId: Uuid } & QuoteLineInput, SupplierQuoteDetail>(
    ({ quoteId }) => `/supplier-quotes/${quoteId}/lines`,
    "POST",
    ({ quoteId: _quoteId, ...rest }) => rest,
  );
}

/** Corriger une ligne. C'est le geste de la revue humaine. */
export function useUpdateQuoteLine() {
  return useQuoteMutation<
    { quoteId: Uuid; lineId: Uuid } & QuoteLineInput & { reviewed?: boolean },
    SupplierQuoteDetail
  >(
    ({ quoteId, lineId }) => `/supplier-quotes/${quoteId}/lines/${lineId}`,
    "PATCH",
    ({ quoteId: _quoteId, lineId: _lineId, ...rest }) => rest,
  );
}

/** Proposer une structure depuis le document. **Rien n'est validé.** */
export function useExtractQuote() {
  return useQuoteMutation<{ quoteId: Uuid }, SupplierQuoteDetail>(
    ({ quoteId }) => `/supplier-quotes/${quoteId}/extract`,
    "POST",
  );
}

/** Relancer une lecture qui a échoué. Aucune boucle ne le fait toute seule. */
export function useRetryQuoteExtraction() {
  return useQuoteMutation<{ quoteId: Uuid }, SupplierQuoteDetail>(
    ({ quoteId }) => `/supplier-quotes/${quoteId}/extract/retry`,
    "POST",
  );
}

/**
 * **La seule porte vers un prix.** Elle demande un humain autorisé, et ce
 * qu'elle ne fait pas — choisir le fournisseur, approuver un achat, créer une
 * commande — reste vrai après comme avant.
 */
export function useValidateQuote() {
  return useQuoteMutation<{ quoteId: Uuid; note?: string | null }, SupplierQuoteDetail>(
    ({ quoteId }) => `/supplier-quotes/${quoteId}/validate`,
    "POST",
    ({ note }) => ({ note: note || null }),
  );
}

export function useRejectQuote() {
  return useQuoteMutation<{ quoteId: Uuid; reason?: string | null }, SupplierQuoteDetail>(
    ({ quoteId }) => `/supplier-quotes/${quoteId}/reject`,
    "POST",
    ({ reason }) => ({ reason: reason || null }),
  );
}

/** Ouvrir la version suivante. **L'ancienne reste lisible entière.** */
export function useCreateQuoteRevision() {
  return useQuoteMutation<{ quoteId: Uuid; copy_lines?: boolean }, SupplierQuoteDetail>(
    ({ quoteId }) => `/supplier-quotes/${quoteId}/revision`,
    "POST",
    ({ copy_lines }) => ({ copy_lines: copy_lines ?? true }),
  );
}
